import { ICellRendererParams } from 'ag-grid-community';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import { isEmpty } from 'lodash';
import Chip, { Color } from 'lib/Chip';
import SectionButtonChipList from 'lib/SectionButton/SectionButtonChipList';
import { Store } from 'components/common/types/Store.types';
import { groupPOIByParentCategory } from 'utils/poiUtils';
import { formatCurrency, formatNumber } from 'modules/I18N';
import { getChipColor } from 'utils/getChipColor';
import { CriteriaAllocation } from 'utils/formatLineDataToAllocation';
import editSvg from 'assets/icons/edit.svg';
import { sortByCategoryIncludeExclude } from '../../FiltersContent/utils';
import { formatTagsToCategory } from '../../FiltersContent/Tags/utils';
import { POIChipList } from '../../DataTargetingContent/PointsOfInterest/POIChipList';
import { generateRouteFramesSelectedDisplay } from '../../FiltersContent/FrameList/utils/generateRouteFramesSelectedDisplay';

const cellWrapper = 'flex items-center py-1';
const editableCellWrapper = `group relative ${cellWrapper}`;

export const FilterCellRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  if (!value?.length) return null;

  const categorized = sortByCategoryIncludeExclude(value);

  return (
    <div className={cellWrapper}>
      <SectionButtonChipList selected={categorized} maxSelected={10} chipAlign="items-start" />
    </div>
  );
};

export const TagsCellRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  const availableTags = useSelector((state: Store) => state.userOptions.referenceOptions.tags);

  if (!value?.length) return null;

  const tagsWithCategory = !isEmpty(availableTags) ? formatTagsToCategory(value, availableTags) : value;

  const categorized = sortByCategoryIncludeExclude(tagsWithCategory);

  return (
    <div className={cellWrapper}>
      <SectionButtonChipList selected={categorized} maxSelected={10} chipAlign="items-start" />
    </div>
  );
};

export const POICellRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  if (!value?.length) return null;

  const categorized = groupPOIByParentCategory(value);

  return (
    <div className={cellWrapper}>
      <POIChipList selectedPOIByParentCategory={categorized} maxSelected={10} chipAlign="items-start" />
    </div>
  );
};

export const FrameListCellRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  if (!value) return null;

  const routeFrameCodes = value.routeFrameCodes || [];
  const listFiles = value.listFiles || [];

  const generated = generateRouteFramesSelectedDisplay(routeFrameCodes, [], listFiles);
  const categorized = sortByCategoryIncludeExclude(generated);

  if (Object.keys(categorized).length === 0) return null;

  return (
    <div className={cellWrapper}>
      <SectionButtonChipList selected={categorized} maxSelected={10} chipAlign="items-start" />
    </div>
  );
};

interface TargetCellRendererParams extends ICellRendererParams {
  field: 'budget' | 'frames' | 'impressions';
  allocationField: 'budgetAllocation' | 'framesAllocation' | 'impressionsAllocation';
  editedTargets?: Set<string>;
}

export const TargetCellRenderer: React.FC<TargetCellRendererParams> = ({
  value: { value },
  data,
  field,
  allocationField,
  editedTargets,
}) => {
  const { localeCode, currencyCode } = useSelector((state: Store) => state.publisher.configuration);

  const objectiveGroupId = data?.objectiveGroupId;
  const editedKey = objectiveGroupId ? `${objectiveGroupId}-${field}` : '';
  const isEdited = editedTargets?.has(editedKey) ?? false;

  const allocation: CriteriaAllocation = isEdited ? {} : data?.[allocationField] || {};
  const { allocated, status } = allocation;

  const isBudget = field === 'budget';
  let formattedLabel: string | undefined;
  if (value) {
    formattedLabel = isBudget
      ? formatCurrency({ value, localeCode, currencyCode })
      : formatNumber({ value, localeCode });
  }

  let formattedSubLabel: string | undefined;
  if (!isEdited && allocated !== undefined) {
    formattedSubLabel = isBudget
      ? formatCurrency({ value: allocated, localeCode, currencyCode })
      : formatNumber({ value: allocated, localeCode });
  }

  const chipColor = isEdited ? Color.PRIMARY : getChipColor(status);
  const hasIndicator = !isEdited && !!status;

  return (
    <div className={editableCellWrapper}>
      <SVG
        src={editSvg}
        className="absolute left-[-20px] top-1 w-4 h-4 text-neutral-900 opacity-0 transition-opacity pointer-events-none editable-cell-icon"
      />
      {formattedLabel && (
        <Chip label={formattedLabel} subLabel={formattedSubLabel} color={chipColor} hasIndicator={hasIndicator} />
      )}
    </div>
  );
};
